import React, { useMemo, useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { CountryType, StateType, SupportGuarantor, CreateClaimGuarantor } from '../../../../api';
import { getOptions } from '../../../base/select';
import * as S from '../form.styles';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { InputField, SelectField } from '../../../base/form';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import { validateGuarantorFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { formatLookupInfo } from '../../../../stores/claim-editor/claim-editor-lookup-info';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';
import { guarantorIdentityKey } from '../../../../stores/claim-editor/claim-editor-linked-ids';
import { useVerifiedLink } from '../use-verified-link';
import { FormErrorBanner } from '../../../base/form/form-error-banner';


interface GuarantorFormValues {
    guarantorLookupLastName?: string;
    guarantorId?: string;
    first: string;
    middle?: string;
    last: string;
    suffix?: string;
    dob?: string;
    phone?: string;
    email?: string;
    organization?: string;
    externalOrganizationId?: string;
    address1: string;
    address2?: string;
    city: string;
    state?: StateType | '';
    country?: CountryType | '';
    postal: string;
}

const emptyValues: GuarantorFormValues = {
    guarantorLookupLastName: '',
    guarantorId: '',
    first: '',
    middle: '',
    last: '',
    suffix: '',
    dob: '',
    phone: '',
    email: '',
    organization: '',
    externalOrganizationId: '',
    address1: '',
    address2: '',
    city: '',
    state: '' as any,
    country: CountryType.US as any,
    postal: '',
};

/** The form values a canonical guarantor record fills in — from a lookup pick or a fetch by id. */
const valuesFromRecord = (g: SupportGuarantor): GuarantorFormValues => {
    const addr = g.addresses?.[0];
    return {
        guarantorLookupLastName: g.last || '',
        guarantorId: g.id || '',
        first: g.first || '',
        middle: g.middle || '',
        last: g.last || '',
        suffix: g.suffix || '',
        dob: (g.dob as any) || '',
        phone: g.phone || '',
        email: g.email || '',
        organization: g.organization || '',
        externalOrganizationId: (g as any).externalOrganizationId || '',
        address1: addr?.lines?.[0] || '',
        address2: addr?.lines?.[1] || '',
        city: addr?.city || '',
        state: (addr?.state as any) || '' as any,
        country: (addr?.country as any) || '' as any,
        postal: addr?.postal || '',
    };
};

interface IGuarantorFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const GuarantorForm: React.FunctionComponent<IGuarantorFormProps> = observer(({ claimEditorStore }) => {
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const formikRef = useRef<FormikProps<GuarantorFormValues> | null>(null);
    const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);

    const initialValues = useMemo<GuarantorFormValues>(() => {
        const payload = (claimEditorStore?.activePayload as any) || {};
        const g: CreateClaimGuarantor | undefined = (claimEditorStore as any)?.guarantor || payload.guarantor;
        if (!g) return emptyValues;
        const addr = g.addresses?.[0];
        return {
            guarantorLookupLastName: g.last || '',
            guarantorId: (g as any).guarantorId || (g as any).id || '',
            first: g.first || '',
            middle: g.middle || '',
            last: g.last || '',
            suffix: g.suffix || '',
            dob: (g.dob as any) || '',
            phone: g.phone || '',
            email: g.email || '',
            organization: g.organization || '',
            externalOrganizationId: (g as any).externalOrganizationId || '',
            address1: addr?.lines?.[0] || '',
            address2: addr?.lines?.[1] || '',
            city: addr?.city || '',
            state: (addr?.state as any) || '' as any,
            country: (addr?.country as any) || CountryType.US as any,
            postal: addr?.postal || '',
        };
    }, [claimEditorStore?.activePayload, (claimEditorStore as any)?.guarantor]);

    // The claim's own fields cannot vouch for its `guarantorId`; see use-verified-link.
    const { setLink, resolveId, isPending, settled, verificationFailed, retry } = useVerifiedLink(
        initialValues.guarantorId,
        (id: string) => claimEditorStore.getGuarantorById(id),
        (record: SupportGuarantor) => guarantorIdentityKey(valuesFromRecord(record)),
    );

    const convertToGuarantor = (v: GuarantorFormValues): CreateClaimGuarantor => {
        const lines = [v.address1].filter(Boolean);
        if (v.address2) lines.push(v.address2);
        return {
            // The API ignores every other guarantor field while `guarantorId` is present, so the
            // id is sent only while it is verified and the form still describes that record.
            guarantorId: resolveId(v.guarantorId, guarantorIdentityKey(v)),
            first: v.first || '',
            middle: v.middle || undefined as any,
            last: v.last || '',
            suffix: v.suffix?.trim() ? v.suffix.trim() : undefined as any,
            dob: (v.dob as any) || undefined,
            phone: v.phone || undefined,
            email: v.email || undefined,
            organization: v.organization || undefined,
            externalOrganizationId: v.externalOrganizationId || undefined,
            addresses: [
                {
                    name: v.organization || `${(v.first || '').trim()} ${(v.last || '').trim()}`.trim(),
                    lines,
                    city: v.city || '',
                    state: (v.state as any) || undefined,
                    postal: v.postal || '',
                    country: (v.country as any) || undefined,
                } as any,
            ],
        } as CreateClaimGuarantor;
    };

    const persistGuarantor = (values: GuarantorFormValues): void => {
        claimEditorStore.updateGuarantor(convertToGuarantor(values));
    };

    const debouncedSave = useMemo(
        () =>
            _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                // Until the id is verified this could only write the record back WITHOUT it;
                // the `settled` effect below persists again once the answer is known.
                if (isPending()) return;
                persistGuarantor(latestValues);
            }, 500),
        [claimEditorStore],
    );

    useEffect(() => {
        if (settled) debouncedSave();
    }, [settled, debouncedSave]);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
        };
    }, [debouncedSave]);

    return (
        <Formik<GuarantorFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            validate={validateGuarantorFormValues}
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Save initial values to store on mount if store is null/empty
                const hasInitializedRef = useRef(false);
                useEffect(() => {
                    if (!hasInitializedRef.current) {
                        const storeGuarantor = claimEditorStore?.guarantor;
                        // Only save if store is null/empty and we have initial values from payload
                        if (!storeGuarantor && initialValues && (initialValues.first || initialValues.last || initialValues.organization)) {
                            persistGuarantor(initialValues);
                        }
                        hasInitializedRef.current = true;
                    }
                }, []); // Only run once on mount

                useEffect(() => {
                    if (formik.dirty) {
                        debouncedSave();
                    }
                }, [formik.values, formik.dirty, debouncedSave]);

                useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated);

                return (
                <S.FormContainer>
                    {verificationFailed && (
                        <FormErrorBanner
                            messages={['Could not verify the linked guarantor record, so changes to the guarantor are not being saved.']}
                            onRetry={retry}
                        />
                    )}
                    <S.FormGrid>
                        <S.FormField span={2}>
                            <S.Label>Guarantor Lookup</S.Label>
                            <Field
                                name="guarantorLookup"
                                component={AsyncSelectField}
                                placeholder="Type last name to search"
                                loadOptions={async (q: string) => {
                                    const results: SupportGuarantor[] = await claimEditorStore.searchGuarantorsByLastName(q);
                                    return results.map(g => ({
                                        label: [g.first, g.middle, g.last].filter(Boolean).join(' ') || (g.organization || ''),
                                        value: g.id as any,
                                        guarantor: g,
                                        info: formatLookupInfo(g?.phone, g?.email ? _.startCase(_.lowerCase(g.email)) : undefined),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const g: SupportGuarantor = opt?.guarantor;
                                    if (!g) {
                                        form.setFieldValue('guarantorId', undefined);
                                        return;
                                    }
                                    const newValues = valuesFromRecord(g);
                                    // The picked record IS canonical: link it before persisting.
                                    if (g.id) setLink({ id: g.id, key: guarantorIdentityKey(newValues) });
                                    form.setValues(newValues);
                                    persistGuarantor(newValues);
                                }}
                            />
                        </S.FormField>

                        <S.FormField id={fieldDomId('guarantor.first')}>
                            <S.Label required={!String(formik.values.organization || '').trim()}>First Name</S.Label>
                            <Field name="first" placeholder="First Name" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Middle Name</S.Label>
                            <Field name="middle" placeholder="Middle Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId('guarantor.last')}>
                            <S.Label required={!String(formik.values.organization || '').trim()}>Last Name</S.Label>
                            <Field name="last" placeholder="Last Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId('guarantor.suffix')}>
                            <S.Label>Suffix</S.Label>
                            <Field name="suffix" placeholder="Suffix" component={InputField} maxLength={10 as any} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Date of Birth</S.Label>
                            <Field name="dob" placeholder="YYYY-MM-DD" component={DatePickerField} />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Phone Number</S.Label>
                            <Field
                                name="phone"
                                placeholder="Phone Number"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId('guarantor.email')}>
                            <S.Label>Email</S.Label>
                            <Field name="email" placeholder="Email" component={InputField} type="email" />
                        </S.FormField>

                        <S.FormField span={2}>
                            <S.Label
                                required={
                                    !String(formik.values.first || '').trim() ||
                                    !String(formik.values.last || '').trim()
                                }>
                                Organization Name
                            </S.Label>
                            <Field name="organization" placeholder="Organization Name" component={InputField} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>External Organization System ID</S.Label>
                            <Field name="externalOrganizationId" placeholder="External System ID" component={InputField} />
                        </S.FormField>

                        <S.FormField span={2} id={fieldDomId('guarantor.addresses[0].lines[0]')}>
                            <S.Label required>Address Line 1</S.Label>
                            <Field name="address1" placeholder="Address Line 1" component={InputField} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Address Line 2</S.Label>
                            <Field name="address2" placeholder="Address Line 2" component={InputField} />
                        </S.FormField>

                        <S.FormField id={fieldDomId('guarantor.addresses[0].city')}>
                            <S.Label required>City</S.Label>
                            <Field name="city" placeholder="City" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId('guarantor.addresses[0].state')}>
                            <S.Label required>State</S.Label>
                            <Field
                                name="state"
                                component={SelectField}
                                options={stateOptions as any}
                                placeholder="Select State"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId('guarantor.addresses[0].country')}>
                            <S.Label required>Country</S.Label>
                            <Field
                                name="country"
                                component={SelectField}
                                options={countryOptions as any}
                                placeholder="Select Country"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId('guarantor.addresses[0].postal')}>
                            <S.Label required>Postal Code</S.Label>
                            <Field name="postal" placeholder="Postal Code" component={InputField} />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
    );
});

export default GuarantorForm;
